// =============================================================================
// LP Express — Offer Stack (value-stack close) template renderer.
//
// Renders the SECTION-BASED offer-stack page (see
// shared/lp-express-offer-stack.ts + shared/lp-express-sections.ts) as a
// long-scrolling value-stack close sales page, ported from the template
// library's sales.offer-stack: deal-framing hero (eyebrow + headline + quick
// benefit bullets) → product intro (paragraphs + the brand's REAL product
// photo) → THE VALUE STACK (individually valued rows summed into a
// crossed-out total, discounted to today's price + CTA) → fast-action
// bonuses → an honest scarcity line → reviews (swappable for REAL reviews
// via the shared review picker) → guarantee → FAQ accordion → legal
// disclaimer → sticky bottom CTA bar (mobile).
//
// The component renders ONE section block per invocation (selected via the
// `section` prop), so the page flows through the SAME ordered sections
// pipeline as the product-hero / problem-solution: getLpSections order,
// reorder, per-section hide/delete, and per-section copy regeneration. Copy
// is read via `assembleLpOfferStackContent(generatedContent)` (which reads
// the per-section keys — osHero, osIntro, …) and edited in place through the
// SAME path-based `setText` ops the other templates use (e.g.
// ["osStack","rows",0,"name"]).
//
//  • Imagery: NO AI image slots. The intro section shows the brand's REAL
//    product photo (override key "osIntro:image", swappable via the media
//    picker/upload, never AI-generated).
//  • Prices — FTC-SAFE BY DERIVATION: stack row values are seeded server-side
//    from REAL product data (a bundle's member SKUs with their real prices)
//    and stay operator-editable; the crossed-out TOTAL is computed here at
//    render time by summing the rows' parsed values (never stored, only shown
//    when ≥2 rows carry values AND the total beats today's price); "today's
//    price" and any savings framing come from the real receipt
//    (product price + active sale) exactly like the other templates' buy
//    boxes. Bonuses carry NO value figures, and scarcity is a static editable
//    line — no countdown timer.
//  • Brand adaptation comes from the shared --color-brand-* CSS variables the
//    renderer already sets (brandCssVarsV2).
// =============================================================================
import { useState } from "react";
import {
  Check,
  Gift,
  Loader2,
  Plus,
  RotateCw,
  ShieldCheck,
  Sparkles,
  Star,
} from "lucide-react";
import {
  EditableText,
  EditableElement,
  useLpEditor,
  LpImageActions,
  LpReviewActions,
  useLpMediaOverridesMap,
  useLpReviewOverride,
  type LpReviewChoice,
} from "@/components/lp-editor";
import type { Product } from "@shared/schema";
import type { LpReceipt, LpReviewDisplay } from "@/lib/lp-express-pricing";
import type { LpSectionType } from "@shared/lp-express-sections";
import {
  assembleLpOfferStackContent,
  computeLpOfferStackTotal,
  parseLpOfferStackValue,
  type LpOfferStackContent,
} from "@shared/lp-express-offer-stack";

export type OfferStackCtx = {
  brand: any;
  offer: { checkoutUrl?: string } | any;
  generatedContent: any;
  logoUrl: string | null;
  bundleProductPhotos: string[];
  bundleImage?: string | null;
  products?: Product[];
  receipt?: LpReceipt;
  reviewDisplay?: LpReviewDisplay;
  typography?: { heading?: string; body?: string } | any;
  /** Retry a single failed AI intro-visual slot
   *  (generatedContent.offerStackImages). Undefined on the public page,
   *  where failed/pending slots fall back to the real product photo. */
  onRetrySlot?: (mergeKey: "offerStackImages", slotKey: string) => void;
};

// Shared brand palette (same CSS vars the quiz/listicle/advertorial/
// product-hero/problem-solution templates use, set by the renderer root via
// brandCssVarsV2).
const HEADING = "var(--color-brand-navy)";
const ACCENT = "var(--color-brand-green)";
const DEEP = "var(--color-brand-deepnavy)";
const TEXT = "var(--color-brand-text)";
const MUTED = "var(--color-brand-muted)";
const CARD = "var(--color-brand-card)";
const BORDER = "var(--color-brand-lightborder)";
const CORAL = "var(--color-brand-coral)";
const PAGE_BG = "#FFFFFF";
const SOFT = "#F7F5EF";

// Media-override key for the intro's image slot. The image itself is the AI
// intro-visual (generatedContent.offerStackImages, key "intro-visual"); the
// media picker can override it with a real product photo, and the real photo
// stays the fallback when the AI slot is absent or failed.
const INTRO_PHOTO_SLOT = "osIntro:image";
const INTRO_AI_SLOT_KEY = "intro-visual";

const money = (n: number): string =>
  `$${Number.isInteger(n) ? n.toFixed(0) : n.toFixed(2)}`;

function starRow(rating: number, size = 14) {
  return (
    <span style={{ display: "inline-flex", color: "#F5A623" }}>
      {Array.from({ length: Math.max(1, Math.min(5, rating)) }).map((_, s) => (
        <Star key={s} style={{ width: size, height: size }} className="fill-current" />
      ))}
    </span>
  );
}

// ── CTA button ───────────────────────────────────────────────────────────────
// Editor / no-URL → non-navigating span so copy stays editable.
function CtaButton({
  label,
  path,
  href,
  testId,
}: {
  label: string;
  path: (string | number)[];
  href?: string;
  testId: string;
}) {
  const editor = useLpEditor();
  const inner = (
    <EditableText
      value={label}
      path={path}
      as="span"
      noHideDelete
      placeholder="Get the deal"
      testId={`${testId}-label`}
    />
  );
  const style: React.CSSProperties = {
    display: "inline-block",
    padding: "16px 44px",
    backgroundColor: ACCENT,
    color: "#FFFFFF",
    fontSize: 17,
    fontWeight: 800,
    textAlign: "center",
    borderRadius: 12,
    letterSpacing: "0.02em",
    textDecoration: "none",
    boxShadow: "0 6px 18px rgba(0,0,0,0.16)",
    cursor: "pointer",
  };
  if (editor || !href) {
    return (
      <span
        style={{ ...style, ...(editor ? {} : { opacity: 0.7, cursor: "not-allowed" }) }}
        data-testid={testId}
        data-lp-cta
      >
        {inner}
      </span>
    );
  }
  return (
    <a href={href} style={style} data-testid={testId} data-lp-cta>
      {inner}
    </a>
  );
}

// ── Section heading helper ───────────────────────────────────────────────────
function SectionHeading({
  value,
  path,
  headingFont,
  placeholder,
  testId,
  color = HEADING,
}: {
  value: string;
  path: (string | number)[];
  headingFont: string;
  placeholder: string;
  testId: string;
  color?: string;
}) {
  return (
    <EditableText
      value={value}
      path={path}
      as="h2"
      className={headingFont}
      style={{ fontSize: 28, fontWeight: 800, color, lineHeight: 1.25, margin: "0 0 8px", textAlign: "center", letterSpacing: "-0.01em" }}
      placeholder={placeholder}
      testId={testId}
    />
  );
}

// ── Hero (deal-framing + quick benefit bullets) ─────────────────────────────
function HeroSection({ content, ctx, headingFont, editor }: { content: LpOfferStackContent; ctx: OfferStackCtx; headingFont: string; editor: boolean }) {
  const checkoutUrl: string | undefined = ctx.offer?.checkoutUrl;
  return (
    <div style={{ backgroundColor: PAGE_BG }} data-testid="offer-stack-page">
      {/* Top nav: brand logo/name + shop-now CTA */}
      <div style={{ borderBottom: `1px solid ${BORDER}`, padding: "14px 16px" }}>
        <div style={{ maxWidth: 1080, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 14 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {ctx.logoUrl && <img src={ctx.logoUrl} alt="" style={{ width: 34, height: 34, objectFit: "contain" }} data-testid="img-offer-stack-logo" />}
            <span style={{ fontSize: 17, fontWeight: 800, color: HEADING }}>{ctx.brand?.name ?? ""}</span>
          </div>
          {!editor && checkoutUrl && (
            <a
              href={checkoutUrl}
              style={{ fontSize: 13, fontWeight: 800, color: "#fff", backgroundColor: ACCENT, padding: "9px 18px", borderRadius: 9999, textDecoration: "none" }}
              data-testid="link-offer-stack-nav-cta"
              data-lp-cta
            >
              {content.stack.ctaText || "Shop now"}
            </a>
          )}
        </div>
      </div>

      <div style={{ padding: "64px 20px 56px" }} data-testid="offer-stack-hero">
        <div style={{ maxWidth: 760, margin: "0 auto", textAlign: "center" }}>
          {(content.eyebrow || editor) && (
            <EditableText
              value={content.eyebrow}
              path={["osHero", "eyebrow"]}
              as="p"
              className="uppercase"
              style={{ fontSize: 12, fontWeight: 800, letterSpacing: "0.16em", color: ACCENT, margin: "0 0 14px" }}
              placeholder="The complete kit"
              testId="text-offer-stack-eyebrow"
            />
          )}
          <EditableText
            value={content.headline}
            path={["osHero", "headline"]}
            as="h1"
            className={headingFont}
            multiline
            style={{ fontSize: 40, fontWeight: 900, color: HEADING, lineHeight: 1.12, margin: "0 0 16px", letterSpacing: "-0.02em" }}
            placeholder="Everything you need, one price"
            testId="text-offer-stack-headline"
          />
          {(content.subhead || editor) && (
            <EditableText
              value={content.subhead}
              path={["osHero", "subhead"]}
              as="p"
              multiline
              style={{ fontSize: 18, lineHeight: 1.6, color: MUTED, margin: "0 auto", maxWidth: 620 }}
              placeholder="Every piece of the system, bundled into one simple offer"
              testId="text-offer-stack-subhead"
            />
          )}
          {(content.heroBullets.length > 0 || editor) && (
            <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: "10px 22px", marginTop: 22 }}>
              {content.heroBullets.map((b, i) => (
                <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 7 }} data-testid={`offer-stack-hero-bullet-${i}`}>
                  <Check style={{ width: 15, height: 15, color: ACCENT, flexShrink: 0 }} />
                  <EditableText
                    value={b}
                    path={["osHero", "heroBullets", i]}
                    as="span"
                    style={{ fontSize: 14, fontWeight: 600, color: TEXT }}
                    placeholder="Quick benefit"
                    testId={`text-offer-stack-hero-bullet-${i}`}
                  />
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Product intro (AI intro visual, real-photo fallback) ────────────────────
function IntroSection({ content, ctx, headingFont, editor }: { content: LpOfferStackContent; ctx: OfferStackCtx; headingFont: string; editor: boolean }) {
  const overrides = useLpMediaOverridesMap();
  const photos =
    ctx.bundleProductPhotos.length > 0
      ? ctx.bundleProductPhotos
      : ctx.bundleImage
        ? [ctx.bundleImage]
        : [];
  // AI intro-visual slot. Its spinner/retry state keys off its OWN slot
  // status in generatedContent.offerStackImages — never an aggregate loading
  // flag. Pending/failed slots show spinner/retry only in the BUILDER
  // (onRetrySlot present); the public page is a static snapshot with no
  // polling, so a slot without a URL falls back to the real product photo.
  const aiSlots: any[] = Array.isArray(ctx.generatedContent?.offerStackImages)
    ? ctx.generatedContent.offerStackImages
    : [];
  const aiSlot = aiSlots.find((s: any) => s && s.key === INTRO_AI_SLOT_KEY);
  const aiStatus = aiSlot?.imageStatus as string | undefined;
  const aiUrl =
    aiSlot && aiStatus !== "failed" && aiSlot.imageUrl
      ? (aiSlot.imageUrl as string)
      : null;
  const canRetry = !!(editor && ctx.onRetrySlot);
  const overrideUrl = overrides?.[INTRO_PHOTO_SLOT]?.url as string | undefined;
  const fallbackPhoto = photos[0] ?? null;
  const photo = overrideUrl ?? aiUrl ?? fallbackPhoto;
  const showingAi = !overrideUrl && !!aiUrl;
  // While generating, prefer the builder spinner over the real-photo fallback
  // (the public page never sees this — it has no onRetrySlot).
  const showPending =
    !overrideUrl && !aiUrl && aiStatus === "pending" && !!ctx.onRetrySlot;
  const showFailedRetry = !overrideUrl && !aiUrl && aiStatus === "failed" && canRetry;
  if (!content.intro.heading && content.intro.paragraphs.length === 0 && !editor) return null;
  return (
    <div style={{ backgroundColor: SOFT, padding: "64px 20px" }} data-testid="offer-stack-intro">
      <div style={{ maxWidth: 1000, margin: "0 auto" }}>
        <div className="grid grid-cols-1 lg:grid-cols-2" style={{ gap: 44, alignItems: "center" }}>
          {/* AI intro visual (media-picker override / real photo fallback). */}
          <div className="group/lpimg relative" style={{ borderRadius: 16, overflow: "hidden", border: `1px solid ${BORDER}`, backgroundColor: "#FFFFFF" }}>
            {showPending || showFailedRetry ? (
              <div
                style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 8, height: 400, backgroundColor: SOFT }}
                data-testid="img-offer-stack-product"
              >
                {showFailedRetry ? (
                  <button
                    type="button"
                    onClick={() => ctx.onRetrySlot?.("offerStackImages", INTRO_AI_SLOT_KEY)}
                    className="inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-xs font-semibold"
                    style={{ color: HEADING, border: `1px solid ${BORDER}`, backgroundColor: "#fff" }}
                    data-testid={`button-retry-offer-stack-ai-${INTRO_AI_SLOT_KEY}`}
                  >
                    <RotateCw className="h-3.5 w-3.5" /> Retry image
                  </button>
                ) : (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" style={{ color: MUTED }} />
                    <span style={{ fontSize: 12, color: MUTED }}>Generating image…</span>
                  </>
                )}
              </div>
            ) : photo ? (
              <img
                src={photo}
                alt={showingAi ? ((aiSlot?.altText as string | undefined) || "") : ""}
                style={
                  showingAi
                    ? { display: "block", width: "100%", height: 400, objectFit: "cover" }
                    : { display: "block", width: "100%", height: 400, objectFit: "contain", padding: 20 }
                }
                data-testid="img-offer-stack-product"
              />
            ) : (
              <div
                style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 400 }}
                data-testid="img-offer-stack-product"
              >
                {editor ? <Sparkles className="h-5 w-5" style={{ color: MUTED }} /> : null}
              </div>
            )}
            <LpImageActions
              slotKey={INTRO_PHOTO_SLOT}
              canRegenerate={canRetry}
              onRegenerate={
                canRetry
                  ? () => ctx.onRetrySlot?.("offerStackImages", INTRO_AI_SLOT_KEY)
                  : undefined
              }
              productPhotos={ctx.bundleProductPhotos}
              revertLabel="Use default"
              revertTitle="Revert to the AI-generated image"
              testIdPrefix="offer-stack-product-photo"
            />
          </div>
          <div>
            <p className="uppercase" style={{ fontSize: 12, fontWeight: 800, letterSpacing: "0.16em", color: ACCENT, margin: "0 0 10px" }}>
              What's inside
            </p>
            <EditableText
              value={content.intro.heading}
              path={["osIntro", "heading"]}
              as="h2"
              className={headingFont}
              style={{ fontSize: 30, fontWeight: 900, color: HEADING, lineHeight: 1.2, margin: "0 0 14px", letterSpacing: "-0.01em" }}
              placeholder="Why we bundled it"
              testId="text-offer-stack-intro-heading"
            />
            {content.intro.paragraphs.map((p, i) => (
              <EditableText
                key={i}
                value={p}
                path={["osIntro", "paragraphs", i]}
                as="p"
                multiline
                style={{ fontSize: 16, lineHeight: 1.7, color: TEXT, margin: "0 0 14px" }}
                placeholder="Intro paragraph"
                testId={`text-offer-stack-intro-p-${i}`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── The value stack (rows + derived total + real today's price + CTA) ───────
function StackSection({ content, ctx, headingFont, editor }: { content: LpOfferStackContent; ctx: OfferStackCtx; headingFont: string; editor: boolean }) {
  const receipt = ctx.receipt;
  const checkoutUrl: string | undefined = ctx.offer?.checkoutUrl;
  const rows = content.stack.rows;
  if (rows.length === 0 && !editor) return null;
  // FTC-safe derivation: the total is COMPUTED from the rows' real values
  // (≥2 valued rows required), and only crossed out when it actually beats
  // today's real price.
  const total = computeLpOfferStackTotal(rows);
  const todayPrice = receipt ? parseLpOfferStackValue(receipt.priceTodayLabel) : null;
  const showTotal = total !== null && (todayPrice === null || total > todayPrice);
  const showSave =
    total !== null && todayPrice !== null && total > todayPrice;
  return (
    <div style={{ backgroundColor: PAGE_BG, padding: "64px 20px" }} data-testid="offer-stack-stack">
      <div style={{ maxWidth: 680, margin: "0 auto" }}>
        <SectionHeading
          value={content.stack.heading}
          path={["osStack", "heading"]}
          headingFont={headingFont}
          placeholder="Here's everything you get"
          testId="text-offer-stack-stack-heading"
        />
        {(content.stack.subhead || editor) && (
          <EditableText
            value={content.stack.subhead}
            path={["osStack", "subhead"]}
            as="p"
            style={{ fontSize: 15, lineHeight: 1.6, color: MUTED, margin: "0 0 6px", textAlign: "center" }}
            placeholder="Every piece, itemized"
            testId="text-offer-stack-stack-subhead"
          />
        )}
        <div style={{ border: `2px solid ${ACCENT}`, borderRadius: 18, backgroundColor: CARD, padding: "10px 24px 28px", marginTop: 24 }}>
          {rows.map((r, i) => (
            <div
              key={i}
              style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 14, padding: "16px 0", borderBottom: `1px solid ${BORDER}` }}
              data-testid={`offer-stack-row-${i}`}
            >
              <span style={{ display: "inline-flex", alignItems: "center", gap: 10, minWidth: 0, flex: 1 }}>
                <span style={{ width: 26, height: 26, borderRadius: "50%", backgroundColor: "rgba(45,122,79,0.1)", display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <Check style={{ width: 14, height: 14, color: ACCENT }} />
                </span>
                <EditableText
                  value={r.name}
                  path={["osStack", "rows", i, "name"]}
                  as="span"
                  style={{ fontSize: 15, fontWeight: 700, color: HEADING }}
                  placeholder="Included item"
                  testId={`text-offer-stack-row-name-${i}`}
                />
              </span>
              {(r.value || editor) && (
                <EditableText
                  value={r.value}
                  path={["osStack", "rows", i, "value"]}
                  as="span"
                  style={{ fontSize: 14, fontWeight: 700, color: MUTED, whiteSpace: "nowrap" }}
                  placeholder="$0"
                  testId={`text-offer-stack-row-value-${i}`}
                />
              )}
            </div>
          ))}

          {/* Derived total + REAL today's price. */}
          <div style={{ textAlign: "center", paddingTop: 24 }}>
            {showTotal && total !== null && (
              <p style={{ fontSize: 15, color: MUTED, margin: "0 0 6px" }} data-testid="text-offer-stack-total">
                Total value:{" "}
                <span style={{ textDecoration: "line-through", fontWeight: 700 }}>{money(total)}</span>
              </p>
            )}
            {receipt && (
              <div style={{ display: "flex", alignItems: "baseline", justifyContent: "center", gap: 10, margin: "0 0 18px" }}>
                <span style={{ fontSize: 15, color: TEXT, fontWeight: 600 }}>Today:</span>
                <span style={{ fontSize: 34, fontWeight: 900, color: HEADING }} data-testid="text-offer-stack-price">
                  {receipt.priceTodayLabel}
                </span>
                {receipt.hasDiscount && receipt.retailLabel && !showTotal && (
                  <span style={{ fontSize: 18, color: "#999", textDecoration: "line-through" }}>{receipt.retailLabel}</span>
                )}
                {showSave && total !== null && todayPrice !== null && (
                  <span
                    style={{ backgroundColor: CORAL, color: "#fff", fontWeight: 800, fontSize: 11, padding: "3px 9px", borderRadius: 5, letterSpacing: "0.06em" }}
                    data-testid="badge-offer-stack-save"
                  >
                    SAVE {money(Math.round((total - todayPrice) * 100) / 100)}
                  </span>
                )}
              </div>
            )}
            <CtaButton
              label={content.stack.ctaText}
              path={["osStack", "ctaText"]}
              href={checkoutUrl}
              testId="button-offer-stack-cta"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Fast-action bonuses (NO value figures — FTC-safe) ───────────────────────
function BonusesSection({ content, headingFont, editor }: { content: LpOfferStackContent; headingFont: string; editor: boolean }) {
  if (content.bonuses.items.length === 0 && !editor) return null;
  return (
    <div style={{ backgroundColor: SOFT, padding: "56px 20px" }} data-testid="offer-stack-bonuses">
      <div style={{ maxWidth: 760, margin: "0 auto" }}>
        <SectionHeading
          value={content.bonuses.heading}
          path={["osBonuses", "heading"]}
          headingFont={headingFont}
          placeholder="Order today and also get"
          testId="text-offer-stack-bonuses-heading"
        />
        {(content.bonuses.subhead || editor) && (
          <EditableText
            value={content.bonuses.subhead}
            path={["osBonuses", "subhead"]}
            as="p"
            style={{ fontSize: 15, lineHeight: 1.6, color: MUTED, margin: "0 0 6px", textAlign: "center" }}
            placeholder="Included with every order"
            testId="text-offer-stack-bonuses-subhead"
          />
        )}
        <div style={{ marginTop: 24 }}>
          {content.bonuses.items.map((b, i) => (
            <div
              key={i}
              style={{ display: "flex", alignItems: "flex-start", gap: 14, backgroundColor: CARD, border: `1px solid ${BORDER}`, borderRadius: 14, padding: "18px 20px", marginBottom: 14 }}
              data-testid={`offer-stack-bonus-${i}`}
            >
              <span style={{ width: 38, height: 38, borderRadius: 10, backgroundColor: "rgba(45,122,79,0.1)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <Gift style={{ width: 18, height: 18, color: ACCENT }} />
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <EditableText
                  value={b.title}
                  path={["osBonuses", "items", i, "title"]}
                  as="h3"
                  className={headingFont}
                  style={{ fontSize: 17, fontWeight: 800, color: HEADING, margin: "0 0 4px" }}
                  placeholder="Bonus title"
                  testId={`text-offer-stack-bonus-title-${i}`}
                />
                <EditableText
                  value={b.body}
                  path={["osBonuses", "items", i, "body"]}
                  as="p"
                  multiline
                  style={{ fontSize: 14, lineHeight: 1.65, color: TEXT, margin: 0 }}
                  placeholder="What the bonus is and why it helps"
                  testId={`text-offer-stack-bonus-body-${i}`}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Scarcity (STATIC honest line — no countdown timer) ──────────────────────
function ScarcitySection({ content, editor }: { content: LpOfferStackContent; editor: boolean }) {
  if (!content.scarcity.line && !editor) return null;
  return (
    <div style={{ backgroundColor: DEEP, padding: "26px 20px" }} data-testid="offer-stack-scarcity">
      <EditableText
        value={content.scarcity.line}
        path={["osScarcity", "line"]}
        as="p"
        multiline
        style={{ maxWidth: 760, margin: "0 auto", fontSize: 16, fontWeight: 700, lineHeight: 1.6, color: "#FFFFFF", textAlign: "center" }}
        placeholder="Bundles are made in small batches — when this run sells through, the next one ships later"
        testId="text-offer-stack-scarcity"
      />
    </div>
  );
}

// ── Reviews (real reviews win via the shared review picker) ─────────────────
function ReviewCard({
  slot,
  item,
  options,
}: {
  slot: number;
  item?: { name: string; role: string; text: string; rating: number };
  options: LpReviewChoice[];
}) {
  const editor = useLpEditor();
  const slotKey = `osReviews:items:${slot}`;
  const override = useLpReviewOverride(slotKey);
  const rev: LpReviewChoice | null = override ?? options[slot] ?? null;
  const AVATARS = ["#E8927C", "#7BBFB2", "#A78BCA", "#E0A45E", "#8CB4D9", "#C4A265"];
  if (!rev && !item && !editor) return null;
  const name = rev ? rev.reviewer || "Verified customer" : item?.name ?? "";
  const rating = rev ? rev.rating : item?.rating ?? 5;
  const base: (string | number)[] = ["osReviews", "items", slot];
  return (
    <div
      className="group/lpreview relative"
      style={{ backgroundColor: CARD, borderRadius: 14, border: `1px solid ${BORDER}`, padding: "18px 20px" }}
      data-testid={`offer-stack-review-${slot}`}
    >
      {options.length > 0 && (
        <LpReviewActions slotKey={slotKey} reviews={options} testIdPrefix={`offer-stack-review-${slot}`} />
      )}
      <div style={{ marginBottom: 8 }}>{starRow(rating, 13)}</div>
      {rev ? (
        <p style={{ fontSize: 14, lineHeight: 1.65, color: TEXT, margin: "0 0 12px" }}>"{rev.reviewText}"</p>
      ) : (
        <EditableText
          value={item?.text ?? ""}
          path={[...base, "text"]}
          as="p"
          multiline
          style={{ fontSize: 14, lineHeight: 1.65, color: TEXT, margin: "0 0 12px" }}
          placeholder="Testimonial quote"
          testId={`text-offer-stack-review-text-${slot}`}
        />
      )}
      <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
        <div
          style={{ width: 34, height: 34, borderRadius: "50%", backgroundColor: AVATARS[slot % AVATARS.length], display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
        >
          <span style={{ fontSize: 13, fontWeight: 800, color: "#fff" }}>{(name || "A")[0]}</span>
        </div>
        <div style={{ minWidth: 0 }}>
          {rev ? (
            <p style={{ fontSize: 13, fontWeight: 700, color: HEADING, margin: 0 }}>{name}</p>
          ) : (
            <EditableText
              value={item?.name ?? ""}
              path={[...base, "name"]}
              as="p"
              style={{ fontSize: 13, fontWeight: 700, color: HEADING, margin: 0 }}
              placeholder="Customer name"
              testId={`text-offer-stack-review-name-${slot}`}
            />
          )}
          {!rev && (item?.role || editor) && (
            <EditableText
              value={item?.role ?? ""}
              path={[...base, "role"]}
              as="p"
              style={{ fontSize: 11, color: MUTED, margin: 0 }}
              placeholder="Verified buyer"
              testId={`text-offer-stack-review-role-${slot}`}
            />
          )}
          {rev && <p style={{ fontSize: 11, color: ACCENT, margin: 0, fontWeight: 600 }}>Verified buyer</p>}
        </div>
      </div>
    </div>
  );
}

function ReviewsSection({ content, ctx, headingFont }: { content: LpOfferStackContent; ctx: OfferStackCtx; headingFont: string }) {
  const reviewDisplay = ctx.reviewDisplay;
  const reviewOptions = (reviewDisplay?.allReviews ?? []) as LpReviewChoice[];
  const items = Array.isArray(content.reviews.items) ? content.reviews.items : [];
  if (items.length === 0 && reviewOptions.length === 0) return null;
  return (
    <EditableElement elementKey="offer-stack-reviews" toolbarSide="left">
      <div style={{ backgroundColor: SOFT, padding: "56px 20px" }} data-testid="offer-stack-reviews">
        <div style={{ maxWidth: 1080, margin: "0 auto" }}>
          <SectionHeading
            value={content.reviews.heading}
            path={["osReviews", "heading"]}
            headingFont={headingFont}
            placeholder="What customers are saying"
            testId="text-offer-stack-reviews-heading"
          />
          {/* Aggregate line — DERIVED from the brand's REAL review data only. */}
          {reviewDisplay?.hasReviews && (
            <p style={{ fontSize: 15, color: MUTED, textAlign: "center", margin: "0 0 8px" }} data-testid="text-offer-stack-reviews-agg">
              <span style={{ color: "#F5A623" }}>★★★★★</span> {reviewDisplay.ratingText} · {reviewDisplay.reviewCountText} reviews
            </p>
          )}
          <div className="grid grid-cols-1 md:grid-cols-3" style={{ gap: 18, marginTop: 28 }}>
            {Array.from({
              length: Math.min(3, Math.max(items.length, reviewOptions.length)),
            }).map((_, i) => (
              <ReviewCard key={i} slot={i} item={items[i]} options={reviewOptions} />
            ))}
          </div>
        </div>
      </div>
    </EditableElement>
  );
}

// ── Guarantee ────────────────────────────────────────────────────────────────
function GuaranteeSection({ content, headingFont, editor }: { content: LpOfferStackContent; headingFont: string; editor: boolean }) {
  if (!content.guarantee.heading && !content.guarantee.body && !editor) return null;
  return (
    <div style={{ backgroundColor: PAGE_BG, padding: "48px 20px" }}>
      <div
        style={{ maxWidth: 760, margin: "0 auto", display: "flex", alignItems: "center", gap: 24, backgroundColor: CARD, border: `1.5px solid ${BORDER}`, borderRadius: 16, padding: "28px 26px", flexWrap: "wrap" }}
        data-testid="offer-stack-guarantee"
      >
        <div
          style={{ width: 96, height: 96, borderRadius: "50%", backgroundColor: "rgba(45,122,79,0.1)", border: `2px solid ${ACCENT}`, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
        >
          <span style={{ fontSize: 22, fontWeight: 900, color: ACCENT, lineHeight: 1 }}>{content.guarantee.sealTop || "30"}</span>
          <span style={{ fontSize: 10, fontWeight: 700, color: ACCENT, textTransform: "uppercase", letterSpacing: "0.06em" }}>{content.guarantee.sealBottom || "Day"}</span>
        </div>
        <div style={{ flex: 1, minWidth: 220 }}>
          <EditableText
            value={content.guarantee.heading}
            path={["osGuarantee", "heading"]}
            as="h3"
            className={headingFont}
            style={{ fontSize: 21, fontWeight: 800, color: HEADING, margin: "0 0 8px" }}
            placeholder="Our guarantee"
            testId="text-offer-stack-guarantee-heading"
          />
          <EditableText
            value={content.guarantee.body}
            path={["osGuarantee", "body"]}
            as="p"
            multiline
            style={{ fontSize: 14, lineHeight: 1.7, color: TEXT, margin: 0 }}
            placeholder="Guarantee body"
            testId="text-offer-stack-guarantee-body"
          />
        </div>
        <ShieldCheck style={{ width: 28, height: 28, color: ACCENT, flexShrink: 0 }} />
      </div>
    </div>
  );
}

// ── FAQ accordion ────────────────────────────────────────────────────────────
function FaqItem({
  index,
  item,
  open,
  onToggle,
}: {
  index: number;
  item: { q: string; a: string };
  open: boolean;
  onToggle: () => void;
}) {
  const editor = useLpEditor();
  const expanded = editor || open;
  const base: (string | number)[] = ["osFaq", "items", index];
  return (
    <div style={{ borderBottom: `1px solid ${BORDER}` }} data-testid={`offer-stack-faq-${index}`}>
      <div
        onClick={editor ? undefined : onToggle}
        style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "16px 0", cursor: editor ? "default" : "pointer" }}
      >
        <EditableText
          value={item.q}
          path={[...base, "q"]}
          as="span"
          style={{ fontSize: 16, fontWeight: 700, color: HEADING }}
          placeholder="Question"
          testId={`text-offer-stack-faq-q-${index}`}
        />
        {!editor && (
          <Plus
            style={{ width: 18, height: 18, color: MUTED, flexShrink: 0, transform: expanded ? "rotate(45deg)" : "none", transition: "transform 0.2s" }}
          />
        )}
      </div>
      {expanded && (
        <EditableText
          value={item.a}
          path={[...base, "a"]}
          as="p"
          multiline
          style={{ fontSize: 14, lineHeight: 1.7, color: TEXT, margin: "0 0 16px" }}
          placeholder="Answer"
          testId={`text-offer-stack-faq-a-${index}`}
        />
      )}
    </div>
  );
}

function FaqSection({ content, headingFont, editor }: { content: LpOfferStackContent; headingFont: string; editor: boolean }) {
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  if (content.faq.items.length === 0 && !editor) return null;
  return (
    <div style={{ backgroundColor: SOFT, padding: "56px 20px" }}>
      <div style={{ maxWidth: 760, margin: "0 auto" }}>
        <SectionHeading
          value={content.faq.heading}
          path={["osFaq", "heading"]}
          headingFont={headingFont}
          placeholder="Frequently asked questions"
          testId="text-offer-stack-faq-heading"
        />
        <div style={{ marginTop: 22 }}>
          {content.faq.items.map((f, i) => (
            <FaqItem
              key={i}
              index={i}
              item={f}
              open={openFaq === i}
              onToggle={() => setOpenFaq((cur) => (cur === i ? null : i))}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Disclaimer ───────────────────────────────────────────────────────────────
function DisclaimerSection({ content, editor }: { content: LpOfferStackContent; editor: boolean }) {
  if (!content.disclaimer && !editor) return null;
  return (
    <div style={{ backgroundColor: PAGE_BG, borderTop: `1px solid ${BORDER}`, padding: "24px 20px" }}>
      <EditableText
        value={content.disclaimer}
        path={["osDisclaimer", "text"]}
        as="p"
        multiline
        style={{ maxWidth: 760, margin: "0 auto", fontSize: 11, lineHeight: 1.6, color: MUTED, textAlign: "center" }}
        placeholder="Legal disclaimer"
        testId="text-offer-stack-disclaimer"
      />
    </div>
  );
}

// ── Sticky bottom CTA bar (mobile) ──────────────────────────────────────────
function StickyBar({ content, ctx }: { content: LpOfferStackContent; ctx: OfferStackCtx }) {
  const editor = useLpEditor();
  const receipt = ctx.receipt;
  const checkoutUrl: string | undefined = ctx.offer?.checkoutUrl;
  const bar = (
    <div
      className="lg:hidden"
      style={{ backgroundColor: DEEP, borderTop: "1px solid rgba(255,255,255,0.1)", boxShadow: "0 -6px 24px rgba(0,0,0,0.25)" }}
      data-testid="offer-stack-sticky-bar"
    >
      <div style={{ maxWidth: 1080, margin: "0 auto", padding: "9px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0, flexWrap: "wrap" }}>
          {receipt?.hasDiscount && receipt.discountPercent != null && (
            <span className="uppercase" style={{ backgroundColor: CORAL, color: "#fff", fontWeight: 900, fontSize: 10, letterSpacing: "0.1em", padding: "2px 7px", borderRadius: 4 }}>
              {receipt.discountPercent}% OFF
            </span>
          )}
          {receipt && <span style={{ color: "#fff", fontWeight: 900, fontSize: 16, lineHeight: 1 }}>{receipt.priceTodayLabel}</span>}
          {receipt?.hasDiscount && receipt.retailLabel && (
            <span style={{ color: "rgba(255,255,255,0.4)", textDecoration: "line-through", fontSize: 11 }}>{receipt.retailLabel}</span>
          )}
        </div>
        {editor || !checkoutUrl ? (
          <span style={{ backgroundColor: ACCENT, color: "#fff", fontWeight: 700, padding: "9px 22px", borderRadius: 9999, fontSize: 13, whiteSpace: "nowrap" }} data-lp-cta>
            <EditableText value={content.stickyCtaText} path={["osStickyCta", "ctaText"]} as="span" noHideDelete placeholder="Get the deal" testId="button-offer-stack-sticky-cta-label" />
          </span>
        ) : (
          <a href={checkoutUrl} style={{ backgroundColor: ACCENT, color: "#fff", fontWeight: 700, padding: "9px 22px", borderRadius: 9999, fontSize: 13, whiteSpace: "nowrap", textDecoration: "none" }} data-testid="button-offer-stack-sticky-cta" data-lp-cta>
            {content.stickyCtaText}
          </a>
        )}
      </div>
    </div>
  );
  if (editor) return <div style={{ marginTop: 12 }}>{bar}</div>;
  return <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 50 }}>{bar}</div>;
}

// ── Main ─────────────────────────────────────────────────────────────────────
// Renders ONE offer-stack section block, selected via `section`. All blocks
// read the whole-page copy shape assembled from the per-section
// generatedContent keys, and edit in place through the per-key paths.
export default function LpExpressOfferStack({
  section,
  ctx,
}: {
  section: LpSectionType;
  ctx: OfferStackCtx;
}) {
  const editor = !!useLpEditor();
  const content = assembleLpOfferStackContent(ctx.generatedContent);
  const headingFont = ctx.typography?.heading ?? "";
  const bodyFont = ctx.typography?.body ?? "";
  const wrap = (node: React.ReactNode) => <div className={bodyFont}>{node}</div>;

  switch (section) {
    case "osHero":
      return wrap(<HeroSection content={content} ctx={ctx} headingFont={headingFont} editor={editor} />);
    case "osIntro":
      return wrap(<IntroSection content={content} ctx={ctx} headingFont={headingFont} editor={editor} />);
    case "osStack":
      return wrap(<StackSection content={content} ctx={ctx} headingFont={headingFont} editor={editor} />);
    case "osBonuses":
      return wrap(<BonusesSection content={content} headingFont={headingFont} editor={editor} />);
    case "osScarcity":
      return wrap(<ScarcitySection content={content} editor={editor} />);
    case "osReviews":
      return wrap(<ReviewsSection content={content} ctx={ctx} headingFont={headingFont} />);
    case "osGuarantee":
      return wrap(<GuaranteeSection content={content} headingFont={headingFont} editor={editor} />);
    case "osFaq":
      return wrap(<FaqSection content={content} headingFont={headingFont} editor={editor} />);
    case "osDisclaimer":
      return wrap(<DisclaimerSection content={content} editor={editor} />);
    case "osStickyCta":
      return wrap(<StickyBar content={content} ctx={ctx} />);
    default:
      return null;
  }
}
